/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-underscore-dangle */
import { action, computed, observable, toJS } from 'mobx';

import { filter } from 'lodash';
import { DateTime, DateTimeUnit } from 'luxon';
import merge from 'deepmerge';
import FiltersStore from 'stores/common/Filters';
import LiveClassStore from 'stores/LiveClass';
import {
  LiveVirtualClass,
  LiveClassOfferingAggregate,
  LiveClassStatus,
} from 'types/liveClass';
import { RangeModifier } from 'react-day-picker';
import type { IRootStore } from 'stores/IRootStore';
import { getFilterConfigurations, RangeFilter } from './config';

type RangeFilters = {
  daterange?: RangeFilter<Date>;
  timerange?: RangeFilter<Date>;
};

class LiveClassOfferingFilterStore extends FiltersStore<LiveVirtualClass> {
  @observable loaded = false;

  @observable _offeringLiveClassAggregate: LiveVirtualClass[] = [];

  @observable hasAppliedFilters: boolean = false;

  @observable hideFullLiveClass: boolean = false;

  @observable guaranteedRunLiveClass: boolean = false;

  @observable rangeFilters: RangeFilters = {};

  // eslint-disable-next-line class-methods-use-this
  @observable filterQueryFunc: any = () => {};

  liveClassStore: LiveClassStore;

  rootStore: IRootStore;

  constructor(liveClassStore: LiveClassStore, rootStore: IRootStore) {
    const { checkboxFilters, rangeFilters } =
      LiveClassOfferingFilterStore.initializeFilters();
    super(checkboxFilters, rootStore.routerStore);
    this.rangeFilters = rangeFilters;
    this.liveClassStore = liveClassStore;
    this.rootStore = rootStore;
  }

  getFilterConfigs = () =>
    getFilterConfigurations(this.rootStore.uiStore?.tFunction);

  @computed get filteredEntries() {
    // if there are no filters applied, return all the sessions
    const entryList = this.hasAppliedFilters
      ? this._offeringLiveClassAggregate
      : this.liveClassStore.offeringLiveClassAggregateList;
    return entryList;
  }

  @action flattenFilters = (key: string) =>
    this.filters[key]?.filter((item) => item.checked).map((item) => item.key) ??
    [];

  /**
   *  Filter the entries based on the date and time ranges.
   */
  @action filterByDateTimeRanges = (
    _filteredEntries: LiveVirtualClass[],
  ): LiveVirtualClass[] => {
    const { daterange } = this.rangeFilters;

    const startDateRange: DateTime = daterange?.from
      ? LiveClassOfferingFilterStore.getStartDate(daterange.from)
      : LiveClassOfferingFilterStore.getStartDate(new Date());

    // the end date might be undefined
    const endDateRange: DateTime | undefined = daterange?.to
      ? LiveClassOfferingFilterStore.getEndDate(daterange.to)
      : undefined;

    const { timerange } = this.rangeFilters;
    const startTimeRange: DateTime | undefined = timerange?.from
      ? DateTime.now().set({
          hour: DateTime.fromJSDate(timerange.from).hour,
          minute: DateTime.fromJSDate(timerange.from).minute,
          second: DateTime.fromJSDate(timerange.from).second,
        })
      : undefined;

    const endTimeRange: DateTime | undefined = timerange?.to
      ? DateTime.now().set({
          hour: DateTime.fromJSDate(timerange.to).hour,
          minute: DateTime.fromJSDate(timerange.to).minute,
          second: DateTime.fromJSDate(timerange.to).second,
        })
      : undefined;

    const filteredByRanges =
      this.rangeFilters.daterange.from ||
      this.rangeFilters.timerange.from ||
      this.rangeFilters.timerange.to
        ? _filteredEntries.filter((entry) => {
            const isBetweenDateRange = this.rangeFilters.daterange.from
              ? LiveClassOfferingFilterStore.isBetweenDateTimeRange(
                  entry.start_time,
                  startDateRange,
                  endDateRange,
                  'day',
                )
              : false;

            if (
              this.rangeFilters.timerange.from ||
              this.rangeFilters.timerange.to
            ) {
              const entryDt = DateTime.fromISO(entry.start_time);
              const sessionStartTime = DateTime.now().set({
                hour: entryDt.hour,
                minute: entryDt.minute,
                second: entryDt.second,
              });

              const isBetweenTimeRange =
                LiveClassOfferingFilterStore.isBetweenDateTimeRange(
                  sessionStartTime,
                  startTimeRange,
                  endTimeRange,
                );

              return this.rangeFilters.daterange.from
                ? isBetweenDateRange && isBetweenTimeRange
                : isBetweenTimeRange;
            }

            return isBetweenDateRange;
          })
        : _filteredEntries;
    return filteredByRanges;
  };

  /**
   * Filter the entries based on the current filters.
   * This applies extra filters in addition to the ones defined in the filter configurations.
   */
  @action filterEntries = () => {
    let _offeringLiveClassAggregate =
      this.liveClassStore.offeringLiveClassAggregateList;

    _offeringLiveClassAggregate = FiltersStore.getFilteredEntries(
      this.liveClassStore.offeringLiveClassAggregateList,
      this.getFilterConfigs(),
      this.filters,
    );

    if (this.hideFullLiveClass) {
      _offeringLiveClassAggregate = filter(
        _offeringLiveClassAggregate,
        (o) => (o.lms_seat_status || '').toLowerCase() !== LiveClassStatus.Full,
      );
    }

    if (this.guaranteedRunLiveClass) {
      _offeringLiveClassAggregate = filter(
        _offeringLiveClassAggregate,
        (o) =>
          (o.status || '').toLowerCase() === LiveClassStatus.GuaranteedToRun,
      );
    }

    const hasRangeFilters =
      this.rangeFilters.daterange?.from !== undefined ||
      this.rangeFilters.timerange?.from !== undefined;

    if (hasRangeFilters) {
      _offeringLiveClassAggregate = _offeringLiveClassAggregate.filter(
        (extendedOffering: LiveClassOfferingAggregate) => {
          const sessionsWithinDateRange = this.filterByDateTimeRanges([
            extendedOffering,
          ]);

          // Only include the offering if it is within the date/time range
          return (
            Array.isArray(sessionsWithinDateRange) &&
            sessionsWithinDateRange.length > 0
          );
        },
      );
    }

    this._offeringLiveClassAggregate = _offeringLiveClassAggregate;
    this.hasAppliedFilters = true;
  };

  @action getFilterInfo = async (filterQueryFunc = () => true) => {
    this.filterQueryFunc = filterQueryFunc;
    const filteredExtendedEntries =
      this.liveClassStore.offeringLiveClassAggregateList.filter(
        filterQueryFunc,
      );

    await this.__getFilterInfo(
      this.rootStore.uiStore.currentLanguage,
      this.rootStore.vocabularyStore,
      filteredExtendedEntries,
    );

    return this.filters;
  };

  @computed get dateRange(): RangeFilter<Date> {
    return this.rangeFilters.daterange;
  }

  @computed get timeRange() {
    return this.rangeFilters.timerange;
  }

  @action setDateRange = (range: RangeModifier) => {
    this.rangeFilters.daterange = merge(
      toJS(this.rangeFilters.daterange),
      range || {},
    );
  };

  @action setStartDate = (startDate: Date) => {
    this.rangeFilters.daterange = {
      ...this.rangeFilters.daterange,
      from: startDate,
    };
  };

  @action setEndDate = (endDate: Date) => {
    this.rangeFilters.daterange = {
      ...this.rangeFilters.daterange,
      to: endDate,
    };
  };

  @action setTimeRange = (range: RangeModifier) => {
    this.rangeFilters.timerange = merge(
      toJS(this.rangeFilters.timerange),
      range || {},
    );
  };

  @action setStartTime = (startTime: Date) => {
    this.rangeFilters.timerange = {
      ...this.rangeFilters.timerange,
      from: startTime,
    };
  };

  @action setEndTime = (endTime: Date) => {
    this.rangeFilters.timerange = {
      ...this.rangeFilters.timerange,
      to: endTime,
    };
  };

  @action resetCalendar = () => {
    if (this.rangeFilters.daterange.from) {
      this.rangeFilters.daterange.from = undefined;
    }
    if (this.rangeFilters.daterange.to) {
      this.rangeFilters.daterange.to = undefined;
    }
  };

  @action resetCalendarTime = () => {
    if (this.rangeFilters.timerange.from) {
      this.rangeFilters.timerange.from = undefined;
    }
    if (this.rangeFilters.timerange.to) {
      this.rangeFilters.timerange.to = undefined;
    }
  };

  @action
  clearFilters(exceptions: string[] = []) {
    this.resetCalendar();
    this.resetCalendarTime();
    super.clearFilters(exceptions, true);
  }

  static toDateTime = (input: DateTime | Date | string): DateTime => {
    if (input instanceof DateTime) return input;
    if (input instanceof Date) return DateTime.fromJSDate(input);
    return DateTime.fromISO(input);
  };

  static getStartDate = (startDate: DateTime | Date | string): DateTime =>
    LiveClassOfferingFilterStore.toDateTime(startDate).set({
      hour: 0,
      minute: 0,
      second: 0,
    });

  static getEndDate = (endDate: DateTime | Date | string): DateTime =>
    LiveClassOfferingFilterStore.toDateTime(endDate).set({
      hour: 23,
      minute: 59,
      second: 59,
    });

  static isBetweenDateTimeRange = (
    dateToCompare: DateTime | Date | string,
    start: DateTime | Date | string | undefined,
    end: DateTime | Date | string | undefined,
    unit?: DateTimeUnit,
  ) => {
    const { toDateTime } = LiveClassOfferingFilterStore;
    const dt = toDateTime(dateToCompare);

    let startsBefore = false;
    if (start) {
      const startDt = toDateTime(start);
      startsBefore = unit
        ? dt.startOf(unit) < startDt.startOf(unit)
        : dt < startDt;
    }

    let endsAfter = false;
    if (end) {
      const endDt = toDateTime(end);
      endsAfter = unit ? dt.startOf(unit) > endDt.startOf(unit) : dt > endDt;
    }

    return !(startsBefore || endsAfter);
  };

  private static initializeFilters() {
    const checkboxFilters = {
      instructors: [],
      languages: [],
    };

    const rangeFilters = {
      daterange: {
        from: undefined,
        to: undefined,
      },
      timerange: {
        from: undefined,
        to: undefined,
      },
    };

    return {
      checkboxFilters,
      rangeFilters,
    };
  }
}

export default LiveClassOfferingFilterStore;
